import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Stack,
  Button,
  Divider,
  IconButton,
  Paper,
  Chip,
  Slide,
  Dialog,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import RemoveIcon from "@mui/icons-material/Remove";

import { useAuthContext } from "../../contexts/AuthContext";
import { ref, get } from "firebase/database";
import { rtdb } from "../../services/firebase/rtdb";

function formatEventDate(startAtMs) {
  if (!startAtMs) return "Data a definir";
  return new Date(Number(startAtMs)).toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

const ONURB_NOTICE = `ATENÇÃO:

• A ONURB PASS não faz parte da organização do evento e não se responsabiliza pelo mesmo, em nível algum, nem pelas informações emitidas pelos organizadores. Mudanças de horário/local são responsabilidade do organizador.

• O organizador/produtor é exclusivamente responsável pelo evento e pela conformidade legal e normativa aplicável (alvarás, licenças, autorizações para menores etc.).

• É obrigatória apresentação do ingresso (impresso ou digital) junto com DOCUMENTO OFICIAL COM FOTO.

• É proibida a venda de bebidas alcoólicas para menores de 18 anos.

• A entrada de menores segue as regras do organizador e da legislação local. Consulte sempre o organizador e certifique-se de eventuais autorizações exigidas.

• Não comparecer invalida o ingresso e não permite reembolso.

• Reembolso: até 7 dias corridos após a compra (direito de arrependimento) e no máximo até 48 horas antes do evento. Após esse prazo, não haverá devolução.

• PCD: entradas seguem a legislação vigente (Lei 12.933/2013 e Decreto 8.537/15) mediante comprovação.

Dúvidas: suporte ONURB PASS via WhatsApp: 0800 232 0800`;

export default function EventDetails() {
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [eventData, setEventData] = useState(null);
  const [ticketLots, setTicketLots] = useState([]);
  const [physicalPoints, setPhysicalPoints] = useState([]);
  const [commissaries, setCommissaries] = useState([]);
  const [lineup, setLineup] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [openBanner, setOpenBanner] = useState(false);
  const [alreadyPurchased, setAlreadyPurchased] = useState(0);

  const { isAuthenticated, loading: authLoading, user } = useAuthContext();

  useEffect(() => {
    if (!eventId) return;

    const load = async () => {
      try {
        const [evSnap, ttSnap, lotsSnap, lineupSnap, commSnap, pointsSnap] =
          await Promise.all([
            get(ref(rtdb, `events/${eventId}`)),
            get(ref(rtdb, `ticketTypes/${eventId}`)),
            get(ref(rtdb, `ticketLots/${eventId}`)),
            get(ref(rtdb, `eventLineup/${eventId}`)),
            get(ref(rtdb, `eventCommissaries/${eventId}`)),
            get(ref(rtdb, `eventPhysicalPoints/${eventId}`)),
          ]);

        if (!evSnap.exists()) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        const event = evSnap.val();

        // normaliza o event para o formato que o componente espera
        setEventData({
          ...event,
          startAtMs: event.timing?.startAtMs ?? null,
          sales: {
            limitPerUser: event.salesSettings?.limitPerUser ?? 2,
            onurbFeePercent: event.salesSettings?.onurbFeePercent ?? 3,
          },
        });

        // monta a lista de ingressos combinando ticketTypes + ticketLots
        const ticketTypesMap = ttSnap.exists() ? ttSnap.val() : {};
        const ticketLotsMap = lotsSnap.exists() ? lotsSnap.val() : {};

        const lots = Object.values(ticketTypesMap)
          .filter((tt) => tt.visibility !== "oculto" && tt.status === "active")
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
          .map((tt) => {
            const lotId = `${tt.ticketTypeId}_lot_1`;
            const lot = ticketLotsMap[lotId] || {};
            return {
              id: lotId,
              name: tt.name || "",
              lot: lot.name || "Lote 1",
              price: lot.price ?? 0,
              quantity: lot.quantity ?? 0,
              soldCount: lot.soldCount ?? 0,
              visibility: tt.visibility || "publico",
            };
          });

        setTicketLots(lots);
        setQuantities(Object.fromEntries(lots.map((l) => [l.id, 0])));

        // lineup
        const lineupVal = lineupSnap.exists() ? lineupSnap.val() : {};
        const lineupItems = Object.values(lineupVal)
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
          .map((item) => ({ id: item.id, label: item.label, position: item.position }));
        setLineup(lineupItems);

        // comissários
        const commVal = commSnap.exists() ? commSnap.val() : {};
        setCommissaries(
          Object.values(commVal)
            .filter((c) => c.active !== false)
            .map((c) => ({ id: c.id, name: c.name || "", phone: c.phone || "" })),
        );

        // pontos físicos
        const pointsVal = pointsSnap.exists() ? pointsSnap.val() : {};
        setPhysicalPoints(
          Object.values(pointsVal)
            .filter((p) => p.active !== false)
            .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
            .map((p) => ({ id: p.id, name: p.name || "" })),
        );
      } catch (err) {
        console.error("Erro ao carregar evento:", err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [eventId]);

  // conta ingressos já comprados para este evento
  useEffect(() => {
    if (!user?.uid || !eventId) return;

    const checkPurchased = async () => {
      try {
        const indexSnap = await get(ref(rtdb, `ticketsByUser/${user.uid}`));
        if (!indexSnap.exists()) return;

        const ids = Object.keys(indexSnap.val());
        const snaps = await Promise.all(ids.map((id) => get(ref(rtdb, `tickets/${id}`))));
        const count = snaps.filter(
          (s) => s.exists() && s.val().eventId === eventId && s.val().status !== "cancelled",
        ).length;
        setAlreadyPurchased(count);
      } catch (_) {}
    };

    checkPurchased();
  }, [user?.uid, eventId]);

  const limitPerUser = eventData?.sales?.limitPerUser ?? 2;
  const remainingAllowance = Math.max(0, limitPerUser - alreadyPurchased);

  const totalIngressos = Object.values(quantities).reduce((a, b) => a + b, 0);

  const subtotal = useMemo(
    () =>
      ticketLots.reduce((acc, t) => acc + t.price * (quantities[t.id] || 0), 0),
    [quantities, ticketLots],
  );

  const taxaOnurb = subtotal * ((eventData?.sales?.onurbFeePercent ?? 3) / 100);
  const total = subtotal + taxaOnurb;

  const minPrice = useMemo(() => {
    if (!ticketLots.length) return 0;
    return Math.min(...ticketLots.map((t) => t.price));
  }, [ticketLots]);

  const cartItems = useMemo(() => {
    return ticketLots
      .filter((ticket) => (quantities[ticket.id] || 0) > 0)
      .map((ticket) => {
        const quantity = quantities[ticket.id] || 0;
        const unitPrice = Number(ticket.price || 0);
        const lineSubtotal = unitPrice * quantity;
        const feePercentValue = Number(eventData?.sales?.onurbFeePercent ?? 3);
        const lineFee = lineSubtotal * (feePercentValue / 100);

        return {
          ticketId: ticket.id,
          name: ticket.name,
          lot: ticket.lot || "",
          quantity,
          unitPrice,
          lineSubtotal,
          lineFee,
          lineTotal: lineSubtotal + lineFee,
        };
      });
  }, [ticketLots, quantities, eventData]);

  function handleGoToCart() {
    if (!eventData || cartItems.length === 0 || authLoading) return;

    const cartPayload = {
      eventId,
      event: {
        id: eventId,
        title: eventData.title,
        bannerUrl: eventData.bannerUrl || "",
        location: eventData.location || {},
        startAtMs: eventData.startAtMs || null,
        classification: eventData.classification || "",
        limitPerUser,
      },
      items: cartItems,
      summary: {
        totalTickets: totalIngressos,
        subtotal,
        feePercent,
        feeAmount: taxaOnurb,
        total,
      },
      createdAt: Date.now(),
    };

    localStorage.setItem("onurbpass_cart", JSON.stringify(cartPayload));

    if (!isAuthenticated) {
      navigate("/login", {
        state: {
          from: { pathname: "/checkout" },
          reason: "checkout_auth_required",
        },
      });
      return;
    }

    navigate("/checkout", {
      state: { cart: cartPayload },
    });
  }

  function addTicket(ticketId) {
    if (totalIngressos >= remainingAllowance) return;
    setQuantities((prev) => ({
      ...prev,
      [ticketId]: (prev[ticketId] || 0) + 1,
    }));
  }

  function removeTicket(ticketId) {
    setQuantities((prev) => ({
      ...prev,
      [ticketId]: Math.max(0, (prev[ticketId] || 0) - 1),
    }));
  }

  // --- Loading ---
  if (loading) {
    return (
      <Box sx={{ py: 10, display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  // --- Not found ---
  if (notFound || !eventData) {
    return (
      <Box sx={{ py: 10, textAlign: "center" }}>
        <Typography variant="h5" sx={{ fontWeight: 900, mb: 1 }}>
          Evento não encontrado
        </Typography>
        <Typography sx={{ opacity: 0.75, mb: 3 }}>
          Esse evento não existe ou foi removido.
        </Typography>
        <Button variant="outlined" onClick={() => navigate("/eventos")}>
          Ver todos os eventos
        </Button>
      </Box>
    );
  }

  const banner =
    eventData.bannerUrl ||
    "https://images.unsplash.com/photo-1492684223066-81342ee5ff30";
  const city = eventData.location?.city || "";
  const state = eventData.location?.state || "";
  const locationLabel = city && state ? `${city} · ${state}` : city;
  const dateLabel = formatEventDate(eventData.startAtMs);
  const feePercent = eventData?.sales?.onurbFeePercent ?? 3;

  return (
    <Box sx={{ pb: { xs: totalIngressos > 0 ? 16 : 10, md: 10 } }}>
      {/* HERO / BANNER */}
      <Box
        role="button"
        aria-label="Abrir banner em tela cheia"
        tabIndex={0}
        onClick={() => setOpenBanner(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") setOpenBanner(true);
        }}
        sx={{
          position: "relative",
          height: { xs: 220, sm: 260, md: 360 },
          backgroundImage: `url(${banner})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          cursor: "zoom-in",
          outline: "none",
          "&:after": {
            content: '"Toque para ampliar"',
            position: "absolute",
            right: 14,
            bottom: 12,
            fontSize: 12,
            fontWeight: 900,
            letterSpacing: "0.04em",
            padding: "8px 10px",
            borderRadius: 999,
            color: "#fff",
            background: "rgba(0,0,0,0.35)",
            border: "1px solid rgba(255,255,255,0.14)",
            backdropFilter: "blur(8px)",
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(8,22,43,0.95) 92%)",
          }}
        />
      </Box>

      <Dialog
        open={openBanner}
        onClose={() => setOpenBanner(false)}
        fullScreen
        PaperProps={{ sx: { background: "rgba(0,0,0,0.96)" } }}
      >
        <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
          <IconButton
            onClick={() => setOpenBanner(false)}
            sx={{
              position: "absolute",
              top: 14,
              right: 14,
              zIndex: 2,
              color: "#fff",
              background: "rgba(255,255,255,0.10)",
              border: "1px solid rgba(255,255,255,0.14)",
              backdropFilter: "blur(8px)",
              "&:hover": { background: "rgba(255,255,255,0.16)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            src={banner}
            alt={`Banner do evento ${eventData.title}`}
            sx={{
              padding: { md: 10, xs: 2 },
              width: "100%",
              height: "100%",
              objectFit: "contain",
              userSelect: "none",
            }}
          />
        </Box>
      </Dialog>

      <Container maxWidth="lg" sx={{ mt: { xs: -5, sm: -4, md: -12 } }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.35fr 0.65fr" },
            gap: 2.5,
            alignItems: "start",
          }}
        >
          {/* LEFT COLUMN */}
          <Stack spacing={2.5}>
            {/* TITLE CARD */}
            <Paper
              sx={{
                p: { xs: 2.2, md: 3 },
                borderRadius: 3,
                paddingTop: { md: 15, sm: 6, xs: 7 },
                boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
              }}
            >
              <Stack spacing={1.2}>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 950, letterSpacing: -0.8 }}
                >
                  {eventData.title}
                </Typography>

                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ opacity: 0.85 }}
                >
                  {locationLabel && (
                    <Chip
                      size="small"
                      label={locationLabel}
                      sx={{ fontWeight: 800 }}
                    />
                  )}
                  <Chip
                    size="small"
                    label={dateLabel}
                    sx={{ fontWeight: 800 }}
                  />
                  {eventData.gatesOpenText && (
                    <Chip
                      size="small"
                      label={eventData.gatesOpenText}
                      sx={{ fontWeight: 800 }}
                    />
                  )}
                  {eventData.classification && (
                    <Chip
                      size="small"
                      label={`🔞 ${eventData.classification} anos`}
                      sx={{ fontWeight: 800 }}
                    />
                  )}
                </Stack>

                {ticketLots.length > 0 && (
                  <Typography sx={{ mt: 0.5, opacity: 0.8 }}>
                    Ingressos a partir de{" "}
                    <Box component="span" sx={{ fontWeight: 900 }}>
                      {minPrice.toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })}
                    </Box>
                  </Typography>
                )}
              </Stack>
            </Paper>

            {/* TICKETS */}
            {ticketLots.length > 0 && (
              <Paper sx={{ p: { xs: 2.2, md: 3 }, borderRadius: 3 }}>
                <Stack spacing={0.8} sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 950 }}>
                    Selecione o ingresso
                  </Typography>
                  <Typography sx={{ fontSize: 13, opacity: 0.75 }}>
                    Reservas apenas após acessar a tela de pagamento •{" "}
                    {remainingAllowance > 0
                      ? `Você pode comprar mais ${remainingAllowance} ingresso${remainingAllowance !== 1 ? "s" : ""}`
                      : "Você atingiu o limite de ingressos para este evento"}
                  </Typography>
                </Stack>

                <Stack spacing={1.5}>
                  {ticketLots.map((t) => (
                    <Box
                      key={t.id}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        border: "1px solid var(--linha)",
                        borderRadius: 2.2,
                        p: 1.8,
                        transition: "all .2s ease",
                        "&:hover": {
                          borderColor: "rgba(255,255,255,0.20)",
                          transform: "translateY(-1px)",
                        },
                      }}
                    >
                      <Box>
                        <Typography sx={{ fontWeight: 950 }}>
                          {t.name}
                        </Typography>
                        <Typography sx={{ opacity: 0.75 }}>
                          {t.price.toLocaleString("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          })}
                        </Typography>
                      </Box>

                      <Stack direction="row" alignItems="center" spacing={1}>
                        <IconButton
                          onClick={() => removeTicket(t.id)}
                          sx={{ border: "1px solid rgba(255,255,255,0.10)" }}
                        >
                          <RemoveIcon />
                        </IconButton>
                        <Typography
                          sx={{
                            fontWeight: 950,
                            minWidth: 18,
                            textAlign: "center",
                          }}
                        >
                          {quantities[t.id] || 0}
                        </Typography>
                        <IconButton
                          onClick={() => addTicket(t.id)}
                          disabled={totalIngressos >= remainingAllowance}
                          sx={{ border: "1px solid rgba(255,255,255,0.10)" }}
                        >
                          <AddIcon />
                        </IconButton>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            )}

            {/* DESCRIPTION */}
            <Paper sx={{ p: { xs: 2.2, md: 3 }, borderRadius: 3 }}>
              <Typography sx={{ fontWeight: 950, mb: 1.2 }}>
                Sobre o evento
              </Typography>
              {eventData.description && (
                <Typography
                  sx={{ whiteSpace: "pre-line", lineHeight: 1.7, fontSize: 15 }}
                >
                  {eventData.description}
                </Typography>
              )}

              {lineup.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography sx={{ fontWeight: 950, mb: 1 }}>
                    Programação
                  </Typography>
                  <Stack spacing={0.5}>
                    {lineup.map((item) => (
                      <Typography key={item.id}>🎵 {item.label}</Typography>
                    ))}
                  </Stack>
                </>
              )}

              {physicalPoints.length > 0 && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography sx={{ fontWeight: 950, mb: 1 }}>
                    Pontos de venda físicos
                  </Typography>
                  <Stack spacing={0.5} sx={{ mb: 2 }}>
                    {physicalPoints.map((p) => (
                      <Typography key={p.id}>• {p.name}</Typography>
                    ))}
                  </Stack>
                </>
              )}

              {commissaries.length > 0 && (
                <>
                  {physicalPoints.length === 0 && <Divider sx={{ my: 3 }} />}
                  <Typography sx={{ fontWeight: 950, mb: 1 }}>
                    Comissários oficiais
                  </Typography>
                  <Stack spacing={0.5}>
                    {commissaries.map((c) => (
                      <Typography key={c.id}>
                        {c.name} – {c.phone}
                      </Typography>
                    ))}
                  </Stack>
                </>
              )}

              {eventData.notes && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography sx={{ fontWeight: 950, mb: 1 }}>
                    Observações
                  </Typography>
                  <Typography
                    sx={{
                      whiteSpace: "pre-line",
                      opacity: 0.85,
                      lineHeight: 1.7,
                    }}
                  >
                    {eventData.notes}
                  </Typography>
                </>
              )}
            </Paper>

            {/* NOTICE */}
            <Paper sx={{ p: { xs: 2.2, md: 3 }, borderRadius: 3 }}>
              <Typography sx={{ fontWeight: 950, mb: 1 }}>
                Informações importantes
              </Typography>
              <Typography
                sx={{ whiteSpace: "pre-line", opacity: 0.85, lineHeight: 1.7 }}
              >
                {eventData.rules?.importantNotice
                  ? `${eventData.rules.importantNotice}\n\n${ONURB_NOTICE}`
                  : ONURB_NOTICE}
              </Typography>
            </Paper>
          </Stack>

          {/* RIGHT COLUMN (STICKY SUMMARY) */}
          <Box
            sx={{
              display: { xs: "none", md: "block" },
              position: { md: "sticky" },
              top: { md: 92 },
              marginTop: 14,
            }}
          >
            <Paper
              sx={{
                p: 2.2,
                borderRadius: 3,
                border: "1px solid rgba(255,255,255,0.08)",
                background: "rgba(255,255,255,0.04)",
                backdropFilter: "blur(10px)",
              }}
            >
              <Typography sx={{ fontWeight: 950, mb: 1 }}>
                Seu pedido
              </Typography>

              <Stack spacing={0.8}>
                <Typography
                  sx={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span>Ingressos</span>
                  <strong>{totalIngressos}</strong>
                </Typography>
                <Typography
                  sx={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span>Subtotal</span>
                  <strong>
                    {subtotal.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </strong>
                </Typography>
                <Typography
                  sx={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span>Taxa ONURB ({feePercent}%)</span>
                  <strong>
                    {taxaOnurb.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </strong>
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 18,
                  }}
                >
                  <span style={{ fontWeight: 950 }}>Total</span>
                  <span style={{ fontWeight: 950 }}>
                    {total.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </span>
                </Typography>
              </Stack>

              <Button
                fullWidth
                size="large"
                sx={{
                  mt: 2,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 950,
                  background:
                    "linear-gradient(-135deg, var(--verdeClaro), #6dffb9)",
                  color: "#1e1e1e",
                  boxShadow: "0 10px 40px rgba(0,255,170,0.45)",
                  "&:hover": { transform: "translateY(-1px)" },
                  "&:disabled": {
                    background: "rgba(255,255,255,0.10)",
                    color: "#1e1e1e",
                    boxShadow: "none",
                    transform: "none",
                  },
                }}
                disabled={totalIngressos === 0 || authLoading}
                onClick={handleGoToCart}
              >
                Ir para o carrinho
              </Button>

              <Typography sx={{ mt: 1.2, fontSize: 12, opacity: 0.7 }}>
                * A taxa ONURB PASS é aplicada por ingresso e já entra no total.
              </Typography>
            </Paper>
          </Box>
        </Box>
      </Container>

      {/* MOBILE BUY BAR */}
      <Slide direction="up" in={totalIngressos > 0} mountOnEnter unmountOnExit>
        <Box
          sx={{
            display: { xs: "block", md: "none" },
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1400,
            pb: "env(safe-area-inset-bottom)",
          }}
        >
          <Box
            sx={{
              background:
                "linear-gradient(180deg, rgba(8,22,43,0) 0%, rgba(8,22,43,0.88) 28%, rgba(8,22,43,0.96) 100%)",
              backdropFilter: "blur(10px)",
              borderTop: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <Paper
              sx={{
                p: 1.6,
                borderRadius: 0,
                border: "1px solid rgba(255,255,255,0.10)",
                background: "rgba(255,255,255,0.04)",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography sx={{ fontWeight: 950, lineHeight: 1.1 }}>
                    Total:{" "}
                    {total.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </Typography>
                  <Typography sx={{ fontSize: 12, opacity: 0.75, mt: 0.3 }}>
                    {totalIngressos} ingresso(s)
                  </Typography>
                  <Typography sx={{ fontSize: 12, opacity: 0.75, mt: 0.3 }}>
                    • Taxa ONURB {feePercent}% inclusa
                  </Typography>
                </Box>

                <Button
                  size="large"
                  sx={{
                    px: 2.2,
                    py: 1.2,
                    borderRadius: 1,
                    fontWeight: 950,
                    background:
                      "linear-gradient(135deg, var(--verdeClaro), #6dffb2)",
                    color: "#042014",
                    boxShadow: "0 10px 40px rgba(0,255,170,0.45)",
                    "&:active": { transform: "scale(0.98)" },
                  }}
                  onClick={handleGoToCart}
                  disabled={authLoading}
                >
                  Comprar agora
                </Button>
              </Stack>
            </Paper>
          </Box>
        </Box>
      </Slide>
    </Box>
  );
}
